/**
 * Step 3: Review & Publish summary.
 * Read-only display of all cadence configuration before publishing.
 */
import PropTypes from 'prop-types';
import { STEP_TYPE_LABELS, getStepIcon, getTaskTypeInfo } from '../../utils/cadence-step-utils';
import { Icon } from '@evertrue/et-components';

const getQualifiedTypeLabel = (step) => {
  if (step.type === 'email') return 'Automated Email';
  if (step.type === 'task') {
    const taskLabel = getTaskTypeInfo(step.taskType)?.label || 'Task';
    return `Manual ${taskLabel}`;
  }
  return STEP_TYPE_LABELS[step.type] || step.type;
};

const getStepDetailText = (step) => {
  if (step.type === 'email') {
    const subject = step.template?.name || step.name;
    return subject ? `Subject: ${subject}` : 'No template selected';
  }
  if (step.type === 'task') return step.name || `${getTaskTypeInfo(step.taskType)?.label || 'Manual'} task`;
  if (step.type === 'wait') {
    return step.waitType === 'until_date'
      ? `Wait until ${step.waitUntil || 'date not set'}`
      : `Wait ${step.waitDuration || 1} ${step.waitUnit || 'days'}`;
  }
  if (step.type === 'condition')
    return step.condition?.label || (step.conditionType || 'always_true').replace(/_/g, ' ');
  if (step.type === 'schedule') return step.scheduleDate || 'No date set';
  return '';
};

const CadenceReviewStep = ({
  cadenceName,
  typeLabel,
  isBlueprint,
  isProspectPlan,
  steps,
  successMetric,
  selectionMethod,
  selectedList,
  selectedSavedSearch,
  enrollContactId,
  getRecipientCount,
  sharedUsers,
  campaignTags,
  removalRules,
  linkedProposals,
}) => {
  const recipientSummary = () => {
    if (isProspectPlan) {
      return enrollContactId ? `Contact ID: ${enrollContactId}` : 'None selected';
    }
    const count = getRecipientCount();
    const method = selectionMethod?.value;
    if (count > 0) {
      const suffix =
        method === 'list'
          ? ` (from list: ${selectedList?.label || 'Unknown'})`
          : method === 'search'
            ? ` (from saved search: ${selectedSavedSearch?.label || 'Unknown'})`
            : '';
      return `${count.toLocaleString()} contact${count !== 1 ? 's' : ''}${suffix}`;
    }
    if (method === 'list' && selectedList?.value) return `List: ${selectedList.label || 'Selected'}`;
    if (method === 'search' && selectedSavedSearch?.value)
      return `Saved search: ${selectedSavedSearch.label || 'Selected'}`;
    return 'None selected (can add after saving)';
  };

  const metricSummary = () => {
    if (!successMetric?.type) return '\u2014';
    const m = successMetric;
    const pct = m.targetPercentage ?? 0;
    const cfg = m.config || {};
    switch (m.type) {
      case 'email_opened':
        return `If ${pct}% of constituents open at least one email, this cadence is successful.`;
      case 'email_replied':
        return `If ${pct}% of constituents reply to at least one email, this cadence is successful.`;
      case 'email_clicked':
        return `If ${pct}% of constituents click a link in at least one email, this cadence is successful.`;
      case 'interaction_created':
        return `If ${pct}% of constituents have an interaction of ${cfg.interactionType || 'any type'} logged during the cadence, this cadence is successful.`;
      case 'gift_given':
        return `If ${pct}% of constituents make a gift of $${cfg.minAmount || '0'} or more to ${cfg.designation || 'any designation'}, this cadence is successful.`;
      case 'portfolio_stage_reached':
        return `If ${pct}% of constituents assigned to ${cfg.teamName || 'a team'} reach the ${cfg.stageName || 'a stage'} stage, this cadence is successful.`;
      case 'saved_search':
        return `If ${pct}% of constituents qualify for the saved search "${cfg.savedSearchName || 'selected'}", this cadence is successful.`;
      case 'gifts_amount':
        return `If ${pct}% of constituents give a total of $${cfg.threshold || '0'} or more, this cadence is successful.`;
      case 'gifts_count':
        return `If ${pct}% of constituents make ${cfg.threshold || '0'} or more gifts, this cadence is successful.`;
      case 'meetings_count':
        return `If ${pct}% of constituents have ${cfg.threshold || '0'} or more meetings, this cadence is successful.`;
      case 'proposal_funded':
        return `If ${pct}% of constituents have a proposal funded, this cadence is successful.`;
      case 'email_bounced':
        return 'Constituents are flagged when their email bounces during the cadence.';
      case 'unsubscribed':
        return 'Constituents are flagged when they unsubscribe during the cadence.';
      default:
        return m.type;
    }
  };

  const stepCount = () => {
    const actionable = new Set(['email', 'task', 'sms', 'mail']);
    const total = steps.reduce((count, step) => count + (actionable.has(step.type) ? 1 : 0), 0);
    return `${total} step${total !== 1 ? 's' : ''}`;
  };

  const renderBranchSteps = (branchSteps, prefix) =>
    branchSteps.map((branchStep, bi) => (
      <div
        key={branchStep.id || `${prefix}-${bi}`}
        className="create-cadence-review--step-item create-cadence-review--step-item-branch"
      >
        <span className="create-cadence-review--step-number create-cadence-review--step-number-branch">
          {branchStep.type === 'condition' ? (
            <span className="create-cadence-review--branch-glyph">&#x2442;</span>
          ) : (
            <Icon
              icon={getStepIcon(branchStep.type, branchStep.taskType || branchStep.eventConfig?.action_type)}
              style={{ fontSize: 14 }}
            />
          )}
        </span>
        <div className="create-cadence-review--step-info">
          <span className="create-cadence-review--step-type">
            <span
              className={`create-cadence-review--branch-badge create-cadence-review--branch-badge-${prefix === 'true' ? 'true' : 'false'}`}
            >
              {prefix === 'true' ? 'TRUE' : 'FALSE'}
            </span>
            {getQualifiedTypeLabel(branchStep)}
          </span>
          <span className="create-cadence-review--step-detail">{getStepDetailText(branchStep)}</span>
        </div>
      </div>
    ));

  return (
    <div className="create-cadence-review">
      <div className="create-cadence-review--card">
        <h3 className="create-cadence-review--card-title">{typeLabel} Summary</h3>

        <div className="create-cadence-review--row">
          <span className="create-cadence-review--label">Name</span>
          <span className="create-cadence-review--value">{cadenceName}</span>
        </div>
        {!isBlueprint && (
          <div className="create-cadence-review--row">
            <span className="create-cadence-review--label">{isProspectPlan ? 'Contact' : 'Recipients'}</span>
            <span className="create-cadence-review--value">{recipientSummary()}</span>
          </div>
        )}
        <div className="create-cadence-review--row">
          <span className="create-cadence-review--label">Success Metric</span>
          <span className="create-cadence-review--value">{metricSummary()}</span>
        </div>
        <div className="create-cadence-review--row">
          <span className="create-cadence-review--label">Sharing</span>
          <span className="create-cadence-review--value">
            {sharedUsers.length > 0
              ? `${sharedUsers.length} ${sharedUsers.length === 1 ? 'person' : 'people'}`
              : 'Restricted'}
          </span>
        </div>
        {campaignTags.length > 0 && (
          <div className="create-cadence-review--row">
            <span className="create-cadence-review--label">Tags</span>
            <span className="create-cadence-review--value">{campaignTags.join(', ')}</span>
          </div>
        )}
        {removalRules.length > 0 && (
          <div className="create-cadence-review--row">
            <span className="create-cadence-review--label">Exit Conditions</span>
            <span className="create-cadence-review--value">
              {removalRules.map((r) => (r.type || '').replace(/_/g, ' ')).join(', ')}
            </span>
          </div>
        )}
        {linkedProposals.length > 0 && (
          <div className="create-cadence-review--row">
            <span className="create-cadence-review--label">Linked Proposals</span>
            <span className="create-cadence-review--value">{linkedProposals.map((p) => p.label).join(', ')}</span>
          </div>
        )}
        <div className="create-cadence-review--row">
          <span className="create-cadence-review--label">Steps</span>
          <span className="create-cadence-review--value">{stepCount()}</span>
        </div>
      </div>

      <div className="create-cadence-review--card">
        <h3 className="create-cadence-review--card-title">Steps Overview</h3>
        {steps.length === 0 ? (
          <p className="create-cadence-review--empty">No steps added yet. Go back to add steps.</p>
        ) : (
          <div className="create-cadence-review--steps-list">
            {(() => {
              return steps.map((step) => {
                return (
                  <div key={step.id}>
                    <div className="create-cadence-review--step-item">
                      <span className="create-cadence-review--step-number">
                        {step.type === 'condition' ? (
                          <span className="create-cadence-review--branch-glyph">&#x2442;</span>
                        ) : (
                          <Icon
                            icon={getStepIcon(step.type, step.taskType || step.eventConfig?.action_type)}
                            style={{ fontSize: 16 }}
                          />
                        )}
                      </span>
                      <div className="create-cadence-review--step-info">
                        <span className="create-cadence-review--step-type">{getQualifiedTypeLabel(step)}</span>
                        <span className="create-cadence-review--step-detail">{getStepDetailText(step)}</span>
                      </div>
                    </div>
                    {step.type === 'condition' && step.branches && (
                      <div className="create-cadence-review--branch-items">
                        {renderBranchSteps(step.branches.true || [], 'true')}
                        {renderBranchSteps(step.branches.false || [], 'false')}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

CadenceReviewStep.propTypes = {
  cadenceName: PropTypes.string,
  typeLabel: PropTypes.string,
  isBlueprint: PropTypes.bool,
  isProspectPlan: PropTypes.bool,
  steps: PropTypes.array,
  successMetric: PropTypes.object,
  selectionMethod: PropTypes.object,
  selectedList: PropTypes.object,
  selectedSavedSearch: PropTypes.object,
  enrollContactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  getRecipientCount: PropTypes.func,
  sharedUsers: PropTypes.array,
  campaignTags: PropTypes.array,
  removalRules: PropTypes.array,
  linkedProposals: PropTypes.array,
};

export default CadenceReviewStep;
